import { Button } from '@/components/ui/Button';
import { formatPrefillWeekdays } from '@/utils/daypartingAutomationPrefill';
import type { WeekdayIndex } from '@/utils/hourlyBudgetWeek';

type CreateDaypartingAutomationModalProps = {
  isOpen: boolean;
  slotCount: number;
  weekdayIndexes: WeekdayIndex[];
  heatmapTimezone: string;
  exceedsMaxWindows: boolean;
  campaignId?: string;
  campaignCount?: number;
  hasPortfolio?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function CreateDaypartingAutomationModal({
  isOpen,
  slotCount,
  weekdayIndexes,
  heatmapTimezone,
  exceedsMaxWindows,
  campaignId,
  campaignCount,
  hasPortfolio,
  onConfirm,
  onCancel,
}: CreateDaypartingAutomationModalProps) {
  if (!isOpen) return null;

  const weekdayLabel = formatPrefillWeekdays(weekdayIndexes);
  const selectedCount = campaignCount ?? (campaignId ? 1 : 0);
  const nextStep = hasPortfolio
    ? "You'll configure campaigns in the selected portfolio next."
    : selectedCount > 1
      ? "You'll configure the selected campaigns next."
      : selectedCount === 1
        ? "You'll configure the selected campaign next."
        : "You'll pick campaigns or a portfolio next, then those hours will be selected in the schedule.";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-xl bg-surface p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-2 text-base font-semibold text-foreground">Create dayparting automation?</h3>
        <p className="mb-3 text-sm text-foreground-muted">
          {slotCount} hour {slotCount === 1 ? 'slot' : 'slots'}
          {weekdayLabel ? ` across ${weekdayLabel}` : ''} will be pre-selected at 1×.{' '}
          {nextStep}
        </p>
        <p className="mb-4 text-xs text-foreground-subtle">
          Hours are copied from the heatmap timezone ({heatmapTimezone}). Dayparting schedules run in
          IST (UTC+5:30).
        </p>
        {exceedsMaxWindows ? (
          <p className="mb-4 text-sm text-warning">
            Some weekdays have more than 12 separate time ranges. You may need to merge hours in the
            editor before saving.
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>Continue</Button>
        </div>
      </div>
    </div>
  );
}
